import * as React from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import * as backofficeApi from '@/services/api/backoffice.api';
import type { ActivityType } from '@/types/story.types';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import type { ActivityFormProps, ActivityTypeOption } from './ActivityForm.types';
import { MultipleChoiceFields } from './MultipleChoiceFields';
import { FillInBlankTapFields } from './FillInBlankTapFields';
import { TrueFalseFields } from './TrueFalseFields';
import { MatchingFields } from './MatchingFields';
import { OrderingFields } from './OrderingFields';
import { AudioTranslationSelectFields } from './AudioTranslationSelectFields';
import { AudioOrderingFields } from './AudioOrderingFields';
import { AudioDictationFields } from './AudioDictationFields';
import { TranslationTapFields } from './TranslationTapFields';
import { TranslationSelectFields } from './TranslationSelectFields';
import { SentenceCompleteTapFields } from './SentenceCompleteTapFields';
import { cn } from '@/utils/cn';
import styles from './ActivityForm.module.scss';

const ACTIVITY_TYPE_OPTIONS: ActivityTypeOption[] = [
  {
    value: 'multiple_choice',
    label: 'Opción múltiple',
    description: 'Pregunta con varias opciones, una respuesta correcta',
  },
  {
    value: 'fill_in_blank_tap',
    label: 'Rellenar huecos (tap)',
    description: 'Párrafo con múltiples huecos — tap para colocar palabras (estilo Duolingo)',
  },
  {
    value: 'true_false',
    label: 'Verdadero/Falso',
    description: 'Afirmación a evaluar como verdadera o falsa',
  },
  {
    value: 'matching',
    label: 'Emparejar',
    description: 'Relacionar elementos de dos columnas',
  },
  {
    value: 'ordering',
    label: 'Ordenar frase (tap)',
    description: 'Ordenar palabras con tap hasta formar la frase correcta',
  },
  {
    value: 'vocabulary_card',
    label: 'Tarjeta de vocabulario',
    description: 'Flash card informacional — no puntuable, avance automático',
  },
  {
    value: 'audio_translation_select',
    label: 'Audio: seleccionar traducción',
    description: 'Escuchar un clip de audio y elegir la traducción correcta al español',
  },
  {
    value: 'audio_ordering',
    label: 'Audio: ordenar frase',
    description: 'Escuchar un clip de audio y ordenar las palabras de la frase con tap',
  },
  {
    value: 'audio_dictation',
    label: 'Audio: dictado',
    description: 'Escuchar un clip de audio y escribir la palabra que se oye',
  },
  {
    value: 'translation_tap',
    label: 'Traducir (tap)',
    description: 'Ver frase en español y construir la traducción al inglés con chips',
  },
  {
    value: 'translation_select',
    label: 'Traducir (selección)',
    description: 'Ver una frase y seleccionar la traducción correcta de 3–4 opciones',
  },
  {
    value: 'sentence_complete_tap',
    label: 'Completar frase (tap)',
    description: 'Frase corta con un solo hueco — elegir la palabra correcta entre chips',
  },
];

const ACTIVITY_TYPE_SELECT_OPTIONS = ACTIVITY_TYPE_OPTIONS.map((opt) => ({
  label: opt.label,
  value: opt.value,
}));

const ACTIVITY_FORM_ID = 'activity-edit-form';

export function ActivityForm({
  mode,
  activity,
  onSave,
  onCancel,
  nextOrder,
  layout = 'default',
  sidebarPortalTarget,
  formId = ACTIVITY_FORM_ID,
  storyId,
}: ActivityFormProps) {
  const [formData, setFormData] = React.useState<ActivityFormData>(() => {
    if (activity) {
      return { ...activity } as ActivityFormData;
    }
    return {
      type: 'multiple_choice',
      question: '',
      explanation: '',
      points: 10,
      order: nextOrder,
      options: ['', '', '', ''],
      correctAnswerIndex: 0,
    };
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const handleTypeChange = (value: string) => {
    const newType = value as ActivityType;
    setFormData((prev): ActivityFormData => {
      const base: ActivityFormData = {
        id: prev.id,
        type: newType,
        question: prev.question,
        explanation: prev.explanation,
        points: prev.points,
        order: prev.order,
      };

      switch (newType) {
        case 'multiple_choice':
          return { ...base, options: ['', '', '', ''], correctAnswerIndex: 0 };
        case 'fill_in_blank_tap':
          return { ...base, textWithBlanks: '', correctAnswers: [''], words: ['', ''] };
        case 'true_false':
          return { ...base, correctAnswer: true };
        case 'matching':
          return { ...base, pairs: [{ left: '', right: '' }] };
        case 'ordering':
          return { ...base, items: [''], correctOrder: [0] };
        case 'vocabulary_card':
          return { ...base };
        case 'audio_translation_select':
          return { ...base, audioUrl: null, audioText: '', options: ['', '', ''], correctAnswerIndex: 0 };
        case 'audio_ordering':
          return { ...base, audioUrl: null, audioText: '', words: ['', ''], correctOrder: [0, 1] };
        case 'audio_dictation':
          return { ...base, audioUrl: null, audioText: '', singleCorrectAnswer: '' };
        case 'translation_tap':
          return { ...base, sourceText: '', words: ['', ''], correctAnswers: [''] };
        case 'translation_select':
          return { ...base, sourceText: '', options: ['', '', ''], correctAnswerIndex: 0 };
        case 'sentence_complete_tap':
          return { ...base, textWithBlanks: '', singleCorrectAnswer: '', words: ['', '', ''] };
        default:
          return base;
      }
    });
  };

  const handleCommonFieldChange = (field: keyof ActivityFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTypeSpecificFieldChange = (updates: Partial<ActivityFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.question.trim()) {
      newErrors.question = 'La pregunta es obligatoria';
    }

    if (formData.points <= 0) {
      newErrors.points = 'Los puntos deben ser mayores que 0';
    }

    // Type-specific validation
    switch (formData.type) {
      case 'multiple_choice':
      case 'audio_translation_select':
      case 'translation_select':
        if (!formData.options || formData.options.length < 2) {
          newErrors.options = 'Se requieren al menos 2 opciones';
        } else if (formData.options.some((opt) => !opt.trim())) {
          newErrors.options = 'Todas las opciones deben tener texto';
        }
        if (formData.type === 'audio_translation_select' && !formData.audioText?.trim()) {
          newErrors.audioText = 'El texto del audio es obligatorio';
        }
        if (formData.type === 'translation_select' && !formData.sourceText?.trim()) {
          newErrors.sourceText = 'La frase fuente es obligatoria';
        }
        break;

      case 'fill_in_blank_tap':
        if (!formData.textWithBlanks?.trim()) {
          newErrors.textWithBlanks = 'El texto con huecos es obligatorio';
        }
        if (!formData.correctAnswers || formData.correctAnswers.length === 0) {
          newErrors.correctAnswers = 'Se requiere al menos una respuesta correcta';
        }
        if (!formData.words || formData.words.length < 2) {
          newErrors.words = 'Se requieren al menos 2 palabras a elegir';
        } else if (formData.words.some((w) => !w.trim())) {
          newErrors.words = 'Todas las palabras deben tener texto';
        }
        break;

      case 'matching':
        if (!formData.pairs || formData.pairs.length < 2) {
          newErrors.pairs = 'Se requieren al menos 2 pares';
        } else if (formData.pairs.some((pair) => !pair.left.trim() || !pair.right.trim())) {
          newErrors.pairs = 'Todos los pares deben tener valor izquierdo y derecho';
        }
        break;

      case 'ordering':
        if (!formData.items || formData.items.length < 2) {
          newErrors.items = 'Se requieren al menos 2 elementos';
        } else if (formData.items.some((item) => !item.trim())) {
          newErrors.items = 'Todos los elementos deben tener texto';
        }
        break;

      case 'audio_ordering':
        if (!formData.audioText?.trim()) {
          newErrors.audioText = 'La frase del audio es obligatoria';
        }
        if (!formData.words || formData.words.length < 2) {
          newErrors.words = 'Se requieren al menos 2 palabras';
        } else if (formData.words.some((w) => !w.trim())) {
          newErrors.words = 'Todas las palabras deben tener texto';
        }
        break;

      case 'audio_dictation':
        if (!formData.audioText?.trim()) {
          newErrors.audioText = 'La palabra del audio es obligatoria';
        }
        if (!formData.singleCorrectAnswer?.trim()) {
          newErrors.singleCorrectAnswer = 'La respuesta correcta es obligatoria';
        }
        break;

      case 'translation_tap':
        if (!formData.sourceText?.trim()) {
          newErrors.sourceText = 'La frase en español es obligatoria';
        }
        if (!formData.correctAnswers || formData.correctAnswers.length === 0) {
          newErrors.correctAnswers = 'Se requiere al menos una palabra de la traducción';
        }
        if (!formData.words || formData.words.length < 2) {
          newErrors.words = 'Se requieren al menos 2 palabras en el pool';
        }
        break;

      case 'sentence_complete_tap':
        if (!formData.textWithBlanks?.trim()) {
          newErrors.textWithBlanks = 'La frase con hueco es obligatoria';
        }
        if (!formData.singleCorrectAnswer?.trim()) {
          newErrors.singleCorrectAnswer = 'La respuesta correcta es obligatoria';
        }
        if (!formData.words || formData.words.length < 2) {
          newErrors.words = 'Se requieren al menos 2 palabras a elegir';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving activity:', error);
      setErrors({ submit: 'Error al guardar la actividad. Por favor, inténtalo de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEditorLayout = layout === 'editor';

  const sidebarContent = (
    <div className={styles.activityForm__sidebar}>
      <div className={styles.activityForm__field}>
        <Select
          label="Tipo de actividad"
          required
          value={formData.type}
          onValueChange={handleTypeChange}
          disabled={mode === 'edit'}
          options={ACTIVITY_TYPE_SELECT_OPTIONS}
        />
        <p className={styles.activityForm__description}>
          {ACTIVITY_TYPE_OPTIONS.find((opt) => opt.value === formData.type)?.description}
        </p>
      </div>
      <div className={styles.activityForm__field}>
          <label htmlFor="question-sidebar" className={styles.activityForm__label}>
            Pregunta <span className={styles.activityForm__required}>*</span>
          </label>
          <Input
            id="question-sidebar"
            value={formData.question}
            onChange={(e) => handleCommonFieldChange('question', e.target.value)}
            placeholder="Introduce la pregunta o instrucción"
            hasError={!!errors.question}
          />
          {errors.question && <span className={styles.activityForm__error}>{errors.question}</span>}
        </div>
      <div className={styles.activityForm__field}>
        <label htmlFor="explanation-sidebar" className={styles.activityForm__label}>
          Explicación (opcional)
        </label>
        <Input
          id="explanation-sidebar"
          value={formData.explanation || ''}
          onChange={(e) => handleCommonFieldChange('explanation', e.target.value)}
          placeholder="Explica la respuesta o proporciona feedback"
        />
      </div>
      <div className={styles.activityForm__field}>
        <label htmlFor="points-sidebar" className={styles.activityForm__label}>
          Puntos <span className={styles.activityForm__required}>*</span>
        </label>
        <Input
          id="points-sidebar"
          type="number"
          value={formData.points}
          onChange={(e) => handleCommonFieldChange('points', parseInt(e.target.value, 10))}
          min={1}
          hasError={!!errors.points}
        />
        {errors.points && <span className={styles.activityForm__error}>{errors.points}</span>}
      </div>
      <div className={styles.activityForm__field}>
        <label htmlFor="order-sidebar" className={styles.activityForm__label}>
          Orden
        </label>
        <Input
          id="order-sidebar"
          type="number"
          value={formData.order}
          onChange={(e) => handleCommonFieldChange('order', parseInt(e.target.value, 10))}
          min={1}
        />
      </div>
    </div>
  );

  return (
    <div className={cn(styles.activityForm, isEditorLayout && styles['activityForm--plain'])}>
      {sidebarPortalTarget &&
        createPortal(sidebarContent, sidebarPortalTarget, 'activity-form-sidebar')}

      <form
        id={formId}
        onSubmit={handleSubmit}
        className={styles.activityForm__form}
      >
        {!isEditorLayout && (
          <div className={styles.activityForm__field}>
            <Select
              label="Tipo de actividad"
              required
              value={formData.type}
              onValueChange={handleTypeChange}
              disabled={mode === 'edit'}
              options={ACTIVITY_TYPE_OPTIONS.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
            />
            <p className={styles.activityForm__description}>
              {ACTIVITY_TYPE_OPTIONS.find((opt) => opt.value === formData.type)?.description}
            </p>
          </div>
        )}

        {!isEditorLayout && (
          <div className={styles.activityForm__field}>
            <label htmlFor="question" className={styles.activityForm__label}>
              Pregunta <span className={styles.activityForm__required}>*</span>
            </label>
            <Input
              id="question"
              value={formData.question}
              onChange={(e) => handleCommonFieldChange('question', e.target.value)}
              placeholder="Introduce la pregunta o instrucción"
              hasError={!!errors.question}
            />
            {errors.question && <span className={styles.activityForm__error}>{errors.question}</span>}
          </div>
        )}

        {!isEditorLayout && (
          <>
            <div className={styles.activityForm__field}>
              <label htmlFor="explanation" className={styles.activityForm__label}>
                Explicación (opcional)
              </label>
              <Input
                id="explanation"
                value={formData.explanation || ''}
                onChange={(e) => handleCommonFieldChange('explanation', e.target.value)}
                placeholder="Explica la respuesta o proporciona feedback"
              />
            </div>
            <div className={styles.activityForm__row}>
              <div className={styles.activityForm__field}>
                <label htmlFor="points" className={styles.activityForm__label}>
                  Puntos <span className={styles.activityForm__required}>*</span>
                </label>
                <Input
                  id="points"
                  type="number"
                  value={formData.points}
                  onChange={(e) => handleCommonFieldChange('points', parseInt(e.target.value, 10))}
                  min={1}
                  hasError={!!errors.points}
                />
                {errors.points && <span className={styles.activityForm__error}>{errors.points}</span>}
              </div>
              <div className={styles.activityForm__field}>
                <label htmlFor="order" className={styles.activityForm__label}>
                  Orden
                </label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => handleCommonFieldChange('order', parseInt(e.target.value, 10))}
                  min={1}
                />
              </div>
            </div>
          </>
        )}

        {/* Type-Specific Fields */}
        <div className={cn(styles.activityForm__typeFields, isEditorLayout && styles['activityForm__typeFields--plain'])}>
          {formData.type === 'multiple_choice' && (
            <MultipleChoiceFields
              options={formData.options || []}
              correctAnswerIndex={formData.correctAnswerIndex || 0}
              onChange={handleTypeSpecificFieldChange}
              error={errors.options}
            />
          )}

          {formData.type === 'fill_in_blank_tap' && (
            <FillInBlankTapFields
              textWithBlanks={formData.textWithBlanks || ''}
              correctAnswers={formData.correctAnswers || []}
              words={formData.words || []}
              onChange={handleTypeSpecificFieldChange}
              errors={{
                textWithBlanks: errors.textWithBlanks,
                correctAnswers: errors.correctAnswers,
                words: errors.words,
              }}
            />
          )}

          {formData.type === 'true_false' && (
            <TrueFalseFields
              correctAnswer={formData.correctAnswer ?? true}
              onChange={handleTypeSpecificFieldChange}
            />
          )}

          {formData.type === 'matching' && (
            <MatchingFields
              pairs={formData.pairs || []}
              onChange={handleTypeSpecificFieldChange}
              error={errors.pairs}
            />
          )}

          {formData.type === 'ordering' && (
            <OrderingFields
              items={formData.items || []}
              onChange={handleTypeSpecificFieldChange}
              error={errors.items}
            />
          )}

          {formData.type === 'audio_translation_select' && (
            <AudioTranslationSelectFields
              audioText={formData.audioText || ''}
              audioUrl={formData.audioUrl}
              options={formData.options || ['', '', '']}
              correctAnswerIndex={formData.correctAnswerIndex ?? 0}
              onChange={handleTypeSpecificFieldChange}
              onGenerateAudio={storyId && mode === 'edit' && formData.id
                ? async () => {
                    const result = await backofficeApi.generateActivityAudio(storyId, formData.id!);
                    return result.audioUrl;
                  }
                : undefined}
              errors={{ audioText: errors.audioText, options: errors.options }}
            />
          )}

          {formData.type === 'audio_ordering' && (
            <AudioOrderingFields
              audioText={formData.audioText || ''}
              audioUrl={formData.audioUrl}
              words={formData.words || []}
              correctOrder={formData.correctOrder || []}
              onChange={handleTypeSpecificFieldChange}
              onGenerateAudio={storyId && mode === 'edit' && formData.id
                ? async () => {
                    const result = await backofficeApi.generateActivityAudio(storyId, formData.id!);
                    return result.audioUrl;
                  }
                : undefined}
              errors={{ audioText: errors.audioText, words: errors.words }}
            />
          )}

          {formData.type === 'audio_dictation' && (
            <AudioDictationFields
              audioText={formData.audioText || ''}
              audioUrl={formData.audioUrl}
              singleCorrectAnswer={formData.singleCorrectAnswer || ''}
              onChange={handleTypeSpecificFieldChange}
              onGenerateAudio={storyId && mode === 'edit' && formData.id
                ? async () => {
                    const result = await backofficeApi.generateActivityAudio(storyId, formData.id!);
                    return result.audioUrl;
                  }
                : undefined}
              errors={{ audioText: errors.audioText, singleCorrectAnswer: errors.singleCorrectAnswer }}
            />
          )}

          {formData.type === 'translation_tap' && (
            <TranslationTapFields
              sourceText={formData.sourceText || ''}
              words={formData.words || []}
              correctAnswers={formData.correctAnswers || []}
              onChange={handleTypeSpecificFieldChange}
              errors={{ sourceText: errors.sourceText, words: errors.words, correctAnswers: errors.correctAnswers }}
            />
          )}

          {formData.type === 'translation_select' && (
            <TranslationSelectFields
              sourceText={formData.sourceText || ''}
              options={formData.options || ['', '', '']}
              correctAnswerIndex={formData.correctAnswerIndex ?? 0}
              onChange={handleTypeSpecificFieldChange}
              errors={{ sourceText: errors.sourceText, options: errors.options }}
            />
          )}

          {formData.type === 'sentence_complete_tap' && (
            <SentenceCompleteTapFields
              textWithBlanks={formData.textWithBlanks || ''}
              singleCorrectAnswer={formData.singleCorrectAnswer || ''}
              words={formData.words || []}
              onChange={handleTypeSpecificFieldChange}
              errors={{ textWithBlanks: errors.textWithBlanks, singleCorrectAnswer: errors.singleCorrectAnswer, words: errors.words }}
            />
          )}

        </div>

        {/* Form Actions - hidden when editor layout (toolbar has Save) */}
        {errors.submit && (
          <div className={styles.activityForm__errorBox}>
            {errors.submit}
          </div>
        )}

        {!isEditorLayout && (
          <div className={styles.activityForm__actions}>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : mode === 'create' ? 'Crear actividad' : 'Guardar cambios'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
